/**
 * Copyright 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { ReactElement } from "react";
import { DraggableProvided } from "react-beautiful-dnd";
import { When } from "react-if";
import { ExtendedTooltip, FlexContainer, theme } from "@nordcloud/gnui";
import { ToggleItemBox, Wrap } from "~/components";
import { PlanEntity, SelectedPlanEntity } from "../../types";

type Props = {
  item: PlanEntity;
  onMouseDown: (item: SelectedPlanEntity) => void;
  isCheckbox?: boolean;
  isSelected?: boolean;
  onDelete?: () => void;
  active?: boolean;
  noDesc?: boolean;
  provided?: DraggableProvided;
  disabled?: boolean;
  tooltip?: string;
  editName?: ReactElement;
  tag?: ReactElement;
};

export function ActionItemEdit({
  item,
  onMouseDown,
  isCheckbox,
  isSelected,
  onDelete,
  active,
  noDesc,
  provided,
  disabled,
  tooltip,
  editName,
  tag,
}: Props) {
  const description = noDesc ? "" : item.description || "";
  const actionName = item.action?.name || item.actionName || item.name;

  const shouldDisplayEditName = editName
    ? editName?.key?.trim() !== actionName?.trim()
    : true;

  return (
    <Wrap>
      <ExtendedTooltip
        caption={tooltip}
        position="start"
        zIndex={theme.zindex.topoftheworld}
      >
        <ToggleItemBox
          active={active}
          provided={provided}
          isSelected={isSelected}
          isCheckbox={isCheckbox}
          item={{
            id: item.id ?? "",
            color: item.color ?? "",
            name: (
              <FlexContainer direction={"row"} justifyContent={"space-between"}>
                <div>
                  <div>
                    {shouldDisplayEditName
                      ? actionName
                      : editName || actionName}
                  </div>

                  {shouldDisplayEditName && (
                    <div
                      style={{
                        opacity: 0.6,
                        fontSize: "0.85rem",
                      }}
                    >
                      {editName}
                    </div>
                  )}
                </div>
                <When condition={!!tag}>{tag}</When>
              </FlexContainer>
            ),
            actionType: item.actionType,
            description,
            icon: item.icon,
            onMouseDown: () => onMouseDown(item),
            onDelete: onDelete,
          }}
          disabled={disabled}
        />
      </ExtendedTooltip>
    </Wrap>
  );
}
